<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useSettingsStore } from '@/stores/settings'
import { i18n } from '@/plugins/i18n'
import type { SupportedLocale } from '@/plugins/i18n'

import AppLayout from '@/layouts/AppLayout.vue'
import PublicLayout from '@/layouts/PublicLayout.vue'
import Toast from '@/components/toast/Toast.vue'
import ConfirmDialog from '@/components/confirm/ConfirmDialog.vue'
import PromptDialog from '@/components/prompt/PromptDialog.vue'

const route = useRoute()
const settingsStore = useSettingsStore()

// Dynamic layout based on route meta
const layout = computed(() => {
  const layoutMeta = route.meta?.layout as string | undefined
  if (layoutMeta === 'public') return PublicLayout
  if (layoutMeta === 'none') return null // No layout wrapper (auth callbacks)
  return AppLayout
})

// Keep vue-i18n locale in sync with the settings store
watch(
  () => settingsStore.language,
  (lang) => {
    i18n.global.locale.value = lang as SupportedLocale
    document.documentElement.lang = lang
  },
  { immediate: true },
)
</script>

<template>
  <!-- Auth callbacks and similar pages: no layout wrapper -->
  <template v-if="!layout">
    <RouterView />
  </template>

  <!-- Wrapped in the appropriate layout -->
  <component v-else :is="layout">
    <RouterView />
  </component>

  <Toast />
  <ConfirmDialog />
  <PromptDialog />
</template>

<style>
.page-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  font-family: 'Plus Jakarta Sans', sans-serif;
  color: var(--text-primary, #333);
}
</style>
